import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Shield,
  LayoutDashboard,
  FileText,
  MessageSquare,
  Users,
  BarChart3,
  Bell,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { USER_ROLE } from '@/utils/constants';
import { notificationsAPI } from '@/services/api';
import { useEffect } from 'react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const markNotificationRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      roles: [USER_ROLE.CLIENT, USER_ROLE.BROKER, USER_ROLE.ADMIN],
    },
    {
      name: 'Submit Claim',
      path: '/submit-claim',
      icon: FileText,
      roles: [USER_ROLE.CLIENT],
    },
    {
      name: 'My Claims',
      path: '/claims',
      icon: FileText,
      roles: [USER_ROLE.CLIENT, USER_ROLE.BROKER, USER_ROLE.ADMIN],
    },
    {
      name: 'Manage Clients',
      path: '/manage-clients',
      icon: Users,
      roles: [USER_ROLE.BROKER, USER_ROLE.ADMIN],
    },
    {
      name: 'Analytics',
      path: '/analytics',
      icon: BarChart3,
      roles: [USER_ROLE.BROKER, USER_ROLE.ADMIN],
    },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 w-64 h-screen bg-slate-900 text-white transition-transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to="/dashboard" className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_broker-claims-1/artifacts/duvrk5eb_kind-insurance-logo.png" 
                alt="Kind Insurance Brokers" 
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span style={{ fontFamily: 'Public Sans, sans-serif' }}>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-800">
            <div className="mb-3">
              <div className="font-semibold text-sm" style={{ fontFamily: 'Public Sans, sans-serif' }}>{user?.full_name}</div>
              <div className="text-xs text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>{user?.email}</div>
              <Badge className="mt-2 bg-blue-600 text-white capitalize">{user?.role}</Badge>
            </div>
            <Button
              data-testid="logout-button"
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            
            <div className="flex-1"></div>

            {/* Notifications */}
            <div className="relative">
              <button
                data-testid="notifications-button"
                className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell className="h-5 w-5 text-slate-600" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-slate-200 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-slate-200">
                    <h3 className="font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h3>
                  </div>
                  {notifications.length > 0 ? (
                    <div className="divide-y divide-slate-100">
                      {notifications.map((notif) => (
                        <div
                          key={notif.id}
                          data-testid={`notification-${notif.id}`}
                          className={`p-3 hover:bg-slate-50 cursor-pointer ${
                            notif.read ? 'opacity-60' : ''
                          }`}
                          onClick={() => markNotificationRead(notif.id)}
                        >
                          <div className="font-medium text-sm mb-1" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                            {notif.title}
                          </div>
                          <div className="text-xs text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                            {notif.message}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-sm text-slate-500" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                      No notifications
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
